// ChapterCard — expandable chapter card for outline review.
//
// Extracted from outline-review.tsx:420-643 to reduce OutlineReviewView
// by ~230 lines and enable future reuse.

import { useCallback, useState } from 'react';
import { ChevronDown, ChevronRight, Star, Settings2, Trash2 } from 'lucide-react';
import type { ChapterAnchor, ConceptAnchor } from '@examforge/shared/schemas';
import { useT } from '../i18n/index.js';

// ─── Labels type ────────────────────────────────────────────────────────────

export interface Labels {
  title: string;
  subtitle: string;
  note: string;
  quickConfirm: string;
  quickConfirmHint: string;
  editConfirm: string;
  confirming: string;
  back: string;
  chapter: string;
  importance: string;
  concepts: string;
  conceptsCount: (n: number) => string;
  noteLabel: string;
  notePlaceholder: string;
  deleteLabel: string;
  deleteConfirm: (t: string) => string;
  invalid: string;
  feedback: string;
  feedbackSent: string;
  advanced: string;
  advancedHint: string;
  bloom: string;
  bloomDesc: string;
  quota: string;
  quotaDesc: string;
  mustCover: string;
  mustCoverDesc: string;
  addConcept: string;
  addConceptPlaceholder: string;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface ChapterCardProps {
  chapter: ChapterAnchor;
  idx: number;
  impOptions: readonly { value: 1 | 2 | 3; label: string; desc: string }[];
  labels: Labels;
  isExpanded: boolean;
  isFocused: boolean;
  onToggle: () => void;
  onUpdate: (patch: Partial<ChapterAnchor>) => void;
  onDelete: () => void;
  onUpdateConcept: (conceptIdx: number, patch: Partial<ConceptAnchor>) => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ChapterCard({
  chapter, idx, impOptions, labels,
  isExpanded, isFocused, onToggle, onUpdate, onDelete, onUpdateConcept,
}: ChapterCardProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [newConcept, setNewConcept] = useState('');
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const t = useT();
  const imp = impOptions.find((o) => o.value === (chapter.importance ?? 2))!;

  const handleAddConcept = useCallback(() => {
    const label = newConcept.trim();
    if (!label) return;
    const id = label.toLowerCase().replace(/[^a-z0-9一-鿿]+/g, '_').slice(0, 64);
    const concepts = [...chapter.concepts, { id, label, aliases: [] }];
    onUpdate({ concepts });
    setNewConcept('');
  }, [newConcept, chapter.concepts, onUpdate]);

  return (
    <div
      className={`or-card${isExpanded ? ' is-expanded' : ''}${isFocused ? ' is-focused' : ''}`}
      role="button" tabIndex={0} aria-expanded={isExpanded}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
    >
      {/* Header */}
      <div className="or-card-head">
        <span className="or-card-num">{idx + 1}</span>
        <span className="or-card-title">{chapter.title}</span>
        <span className="or-card-count">
          {labels.conceptsCount(chapter.concepts.length)}
        </span>
        <span className={`or-card-imp or-card-imp--${chapter.importance ?? 2}`}>{imp.label}</span>
        <button type="button" className="or-card-expand" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="or-card-body" onClick={(e) => e.stopPropagation()}>
          {/* Title */}
          <div className="or-field">
            <label className="or-field-label">{labels.chapter}</label>
            <input
              className="or-field-input or-field-input--title"
              value={chapter.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              maxLength={120}
            />
          </div>

          {/* Importance */}
          <div className="or-field">
            <label className="or-field-label">{labels.importance}</label>
            <div className="or-stars">
              {impOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`or-star${(chapter.importance ?? 2) === opt.value ? ' is-active' : ''}`}
                  onClick={() => onUpdate({ importance: opt.value })}
                  title={opt.desc}
                >
                  <Star size={16} fill={(chapter.importance ?? 2) === opt.value ? 'currentColor' : 'none'} />
                  <span className="or-star-label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Concepts */}
          <div className="or-field">
            <label className="or-field-label">{labels.concepts} ({chapter.concepts.length})</label>
            <div className="or-tags">
              {chapter.concepts.map((concept, cIdx) => (
                <span key={concept.id} className="or-tag">
                  <input
                    className="or-tag-input"
                    value={concept.label}
                    onChange={(e) => onUpdateConcept(cIdx, { label: e.target.value })}
                    maxLength={120}
                  />
                </span>
              ))}
            </div>
            {/* Add concept */}
            <div className="or-add-concept">
              <input
                className="or-add-concept-input"
                value={newConcept}
                onChange={(e) => setNewConcept(e.target.value)}
                placeholder={labels.addConceptPlaceholder}
                maxLength={120}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddConcept(); } }}
              />
              <button type="button" className="or-add-concept-btn" onClick={handleAddConcept} disabled={!newConcept.trim()}>
                {labels.addConcept}
              </button>
            </div>
          </div>

          {/* Note */}
          <div className="or-field">
            <label className="or-field-label">{labels.noteLabel}</label>
            <input
              className="or-field-input"
              value={chapter.userHint ?? ''}
              onChange={(e) => onUpdate({ userHint: (e.target.value || undefined) as never })}
              maxLength={500}
              placeholder={labels.notePlaceholder}
            />
          </div>

          {/* Advanced editing toggle */}
          <button
            type="button"
            className="or-advanced-toggle"
            onClick={() => setAdvancedOpen(!advancedOpen)}
          >
            <Settings2 size={14} />
            {labels.advanced}
            <ChevronDown size={14} className={advancedOpen ? 'or-rotate' : ''} />
          </button>
          <p className="or-advanced-hint">{labels.advancedHint}</p>

          {advancedOpen && (
            <div className="or-advanced-body">
              {/* Bloom levels */}
              <div className="or-field">
                <label className="or-field-label">{labels.bloom}</label>
                <p className="or-field-desc">{labels.bloomDesc}</p>
                <div className="or-bloom-chips">
                  {['recall', 'understand', 'apply', 'analyze', 'evaluate'].map((level) => (
                    <button
                      key={level}
                      type="button"
                      className={`or-bloom-chip${(chapter.focusedCognitiveLevels ?? []).includes(level as never) ? ' is-active' : ''}`}
                      onClick={() => {
                        const current = chapter.focusedCognitiveLevels ?? [];
                        const next = current.includes(level as never)
                          ? current.filter((l) => l !== level)
                          : [...current, level] as typeof current;
                        onUpdate({ focusedCognitiveLevels: next.length > 0 ? next : undefined });
                      }}
                    >{level}</button>
                  ))}
                </div>
              </div>

              {/* Must cover in stages */}
              {chapter.concepts.length > 0 && (
                <div className="or-field">
                  <label className="or-field-label">{labels.mustCover}</label>
                  <p className="or-field-desc">{labels.mustCoverDesc}</p>
                  <div className="or-mustcover">
                    {chapter.concepts.map((concept, cIdx) => {
                      const stages = concept.mustCoverInStages ?? [];
                      return (
                        <div key={concept.id} className="or-mustcover-row">
                          <span className="or-mustcover-label">{concept.label}</span>
                          <div className="or-mustcover-toggles">
                            {['mcqs', 'flashcards'].map((stage) => (
                              <button
                                key={stage}
                                type="button"
                                className={`or-mustcover-btn${stages.includes(stage) ? ' is-active' : ''}`}
                                onClick={() => {
                                  const next = stages.includes(stage)
                                    ? stages.filter((s) => s !== stage)
                                    : [...stages, stage];
                                  onUpdateConcept(cIdx, { mustCoverInStages: next.length > 0 ? next : undefined });
                                }}
                              >{stage === 'mcqs' ? t('chapterCard.stageMcqs') : t('chapterCard.stageFlashcards')}</button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quota override */}
              <div className="or-field">
                <label className="or-field-label">{labels.quota}</label>
                <p className="or-field-desc">{labels.quotaDesc}</p>
                <input
                  className={`or-field-input${quotaError ? ' is-invalid' : ''}`}
                  value={chapter.targetItemCountsOverride ? JSON.stringify(chapter.targetItemCountsOverride as Record<string, number>) : ''}
                  onChange={(e) => {
                    if (!e.target.value) { setQuotaError(null); onUpdate({ targetItemCountsOverride: undefined }); return; }
                    try {
                      const parsed = JSON.parse(e.target.value);
                      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                        setQuotaError(labels.invalid);
                        return;
                      }
                      setQuotaError(null);
                      onUpdate({ targetItemCountsOverride: parsed });
                    } catch { setQuotaError(labels.invalid); }
                  }}
                  placeholder='{"mcqs": 8, "flashcards": 5}'
                />
                {quotaError && <p className="or-field-error">{quotaError}</p>}
              </div>
            </div>
          )}

          {/* Delete */}
          <button type="button" className="or-delete" onClick={onDelete}>
            <Trash2 size={14} /> {labels.deleteLabel}
          </button>
        </div>
      )}
    </div>
  );
}
